import type { AppyCreation, AppyUser } from './model'

export interface TrendingTopic {
  key: string
  label: string
  apps: number
  score: number
}

export interface CreatorRank {
  user: AppyUser
  score: number
  plays: number
  likes: number
  reposts: number
  comments: number
  saves: number
  published: number
}

/** Engagement score for a single published app. */
export const appEngagementScore = (app: Pick<AppyCreation, 'plays' | 'likes' | 'reposts' | 'comments' | 'saves'>): number =>
  app.plays * 1
  + app.likes * 4
  + app.reposts * 5
  + app.comments * 3
  + app.saves * 3

export const publishedApps = (apps: AppyCreation[]): AppyCreation[] =>
  apps.filter((app) => app.status === 'published')

/** Rank published apps by engagement, then recency. */
export const rankTrendingApps = (apps: AppyCreation[], limit = 30): AppyCreation[] =>
  [...publishedApps(apps)]
    .sort((a, b) => {
      const score = appEngagementScore(b) - appEngagementScore(a)
      if (score !== 0) return score
      return Date.parse(b.publishedAt ?? b.updatedAt) - Date.parse(a.publishedAt ?? a.updatedAt)
    })
    .slice(0, limit)

/** Pull #hashtags from free text (captions, posts, titles). */
export const extractHashtags = (...texts: Array<string | null | undefined>): string[] => {
  const found: string[] = []
  const seen = new Set<string>()
  for (const text of texts) {
    if (!text) continue
    for (const match of text.matchAll(/#([a-zA-Z][\w]{1,29})/g)) {
      const tag = match[1]
      const key = tag.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      found.push(tag)
    }
  }
  return found
}

/**
 * Topics people actually use: project tags + #hashtags in post captions/bodies.
 * Only surfaces tags that are popular enough (multi-post or real engagement).
 * Empty feed → empty list (no hardcoded hashtags).
 */
export const rankTrendingTopics = (apps: AppyCreation[], limit = 6): TrendingTopic[] => {
  const byTag = new Map<string, { label: string; apps: number; score: number }>()
  for (const app of publishedApps(apps)) {
    const fromTags = app.tags
      .map((tag) => tag.replace(/^#/, '').trim())
      .filter(Boolean)
      // Ignore synthetic "Post" tag used for text posts
      .filter((tag) => tag.toLowerCase() !== 'post')
    const fromBody = extractHashtags(app.postBody, app.description, app.title)
    const tags = [...new Set([...fromTags, ...fromBody].map((tag) => tag.trim()).filter(Boolean))]
    if (tags.length === 0) continue
    const engagement = appEngagementScore(app)
    for (const tag of tags) {
      const key = tag.toLowerCase()
      const current = byTag.get(key) ?? { label: tag, apps: 0, score: 0 }
      current.apps += 1
      // +1 so a brand-new tag still accumulates; engagement lifts real activity
      current.score += engagement + 1
      // Prefer casing with capitals when present
      if (tag !== tag.toLowerCase()) current.label = tag
      byTag.set(key, current)
    }
  }
  return [...byTag.entries()]
    .map(([key, value]) => ({
      key,
      label: `#${value.label.replace(/\s+/g, '')}`,
      apps: value.apps,
      score: value.score,
    }))
    // Popular/trending: at least 2 posts using it, or one post with real engagement
    .filter((item) => item.apps >= 2 || item.score >= 10)
    .sort((a, b) => b.score - a.score || b.apps - a.apps || a.label.localeCompare(b.label))
    .slice(0, limit)
}

export const isGuestLike = (user: Pick<AppyUser, 'name' | 'handle'>) =>
  /^(?:guest|appy user|new maker|unknown creator)$/i.test(user.name.trim())
  || /^(?:guest_|appy_|maker_|creator_)/i.test(user.handle.trim())

/**
 * Creators worth suggesting: not you, not already followed, and either
 * have social proof or published work (never empty placeholder guests).
 */
export const isFollowCandidate = (user: AppyUser, currentUserId: string): boolean => {
  if (user.id === currentUserId) return false
  if (user.followedByMe) return false
  if (isGuestLike(user)) return false
  return true
}

export interface FeedSignals {
  followedIds?: Iterable<string>
  likedAuthors?: Record<string, number>
  commentedAuthors?: Record<string, number>
  /** postId (or app id) → total dwell ms */
  dwellByPost?: Record<string, number>
  /** local session dwell overrides */
  sessionDwellByPost?: Record<string, number>
}

/**
 * Personalized ranking: who you follow, authors you like/comment on,
 * how long you dwell on posts/games, hashtag affinity, content type,
 * plus base engagement + recency (X-style For You).
 */
export const personalizedScore = (
  app: AppyCreation,
  signals: FeedSignals = {},
): number => {
  const followed = new Set(signals.followedIds ?? [])
  const likedAuthors = signals.likedAuthors ?? {}
  const commentedAuthors = signals.commentedAuthors ?? {}
  const dwellServer = signals.dwellByPost ?? {}
  const dwellSession = signals.sessionDwellByPost ?? {}
  const postKey = app.postId ?? app.id
  const dwellMs = Math.max(Number(dwellServer[postKey] ?? 0), Number(dwellSession[postKey] ?? 0), Number(dwellServer[app.id] ?? 0), Number(dwellSession[app.id] ?? 0))
  const ageHours = Math.max(0, (Date.now() - Date.parse(app.publishedAt ?? app.updatedAt)) / 3_600_000)
  // Stronger recency curve: first 6h boost hard, then soft decay over 72h
  const recency = ageHours < 6
    ? 90 - ageHours * 6
    : Math.max(0, 54 - Math.min(ageHours - 6, 66) * 0.8)

  const isShort = app.postType === 'short'
  const isText = app.postType === 'text'
  const hasMedia = Boolean(app.mediaUrl || app.videoUrl || app.coverUrl)
  const hashtags = extractHashtags(app.postBody, app.description, app.title, ...app.tags)
  // Mild diversity: shorts get a small discovery lift so the feed is not only apps
  const typeBoost = isShort ? 18 : isText ? (hasMedia ? 10 : 4) : 0
  // Verified / staff creators get a small quality signal (not enough to drown others)
  const qualityHint = 0 // reserved for author-level boosts via signals if needed
  // Engagement velocity: log-scale so viral items surface without pure popularity monopoly
  const velocity = Math.log1p(appEngagementScore(app)) * 6
  // Tag richness rewards posts that join conversations (#tags)
  const tagBoost = Math.min(16, hashtags.length * 4)

  return (
    appEngagementScore(app) * 0.85
    + velocity
    + (followed.has(app.authorId) ? 95 : 0)
    + Math.min(48, (likedAuthors[app.authorId] ?? 0) * 9)
    + Math.min(42, (commentedAuthors[app.authorId] ?? 0) * 14)
    + Math.min(60, Math.log1p(dwellMs / 1000) * 12)
    + (app.likedByMe ? 14 : 0)
    + (app.repostedByMe ? 12 : 0)
    + (app.savedByMe ? 10 : 0)
    + recency
    + typeBoost
    + tagBoost
    + qualityHint
    + (hasMedia ? 6 : 0)
  )
}

/** Shorts-only ranking for the vertical feed. */
export const rankShortsFeed = (
  apps: AppyCreation[],
  signals: FeedSignals = {},
  limit = 80,
): AppyCreation[] =>
  rankPersonalizedFeed(
    apps.filter((app) => app.postType === 'short' && app.status === 'published'),
    signals,
    limit,
  )

export const rankPersonalizedFeed = (
  apps: AppyCreation[],
  signals: FeedSignals = {},
  limit = 100,
): AppyCreation[] =>
  [...publishedApps(apps)]
    .sort((a, b) => {
      const score = personalizedScore(b, signals) - personalizedScore(a, signals)
      if (score !== 0) return score
      return Date.parse(b.publishedAt ?? b.updatedAt) - Date.parse(a.publishedAt ?? a.updatedAt)
    })
    .slice(0, limit)

/** Rank who to follow from live follower counts + aggregated app engagement. */
export const rankWhoToFollow = (
  users: AppyUser[],
  apps: AppyCreation[],
  currentUserId: string,
  limit = 5,
): CreatorRank[] => {
  const stats = new Map<string, { plays: number; likes: number; reposts: number; comments: number; saves: number; published: number }>()
  for (const app of publishedApps(apps)) {
    const current = stats.get(app.authorId) ?? { plays: 0, likes: 0, reposts: 0, comments: 0, saves: 0, published: 0 }
    current.plays += app.plays
    current.likes += app.likes
    current.reposts += app.reposts
    current.comments += app.comments
    current.saves += app.saves
    current.published += 1
    stats.set(app.authorId, current)
  }

  return users
    .filter((user) => isFollowCandidate(user, currentUserId))
    .map((user) => {
      const activity = stats.get(user.id) ?? { plays: 0, likes: 0, reposts: 0, comments: 0, saves: 0, published: 0 }
      const score =
        Math.log1p(user.followers) * 14
        + Math.log1p(activity.plays) * 3
        + activity.likes * 4
        + activity.reposts * 5
        + activity.comments * 3
        + activity.saves * 3
        + activity.published * 10
        + (user.verified ? 6 : 0)
      return { user, score, ...activity }
    })
    .filter((row) => row.score > 0 || row.published > 0 || row.user.followers > 0)
    .sort((a, b) => b.score - a.score || b.user.followers - a.user.followers || a.user.handle.localeCompare(b.user.handle))
    .slice(0, limit)
}
